import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Check, Zap, Award, Trophy, Timer, ArrowRight } from "lucide-react";

// Early Bird Configuration
const EARLY_BIRD_DEADLINE = new Date("2026-09-05T23:59:59");
const isEarlyBirdActive = () => new Date() < EARLY_BIRD_DEADLINE;
const getEarlyBirdDiscount = () => {
  if (new Date() < EARLY_BIRD_DEADLINE) {
    return 0.20; // 20% Super Early Bird until Sep 5
  }
  return 0.10; // 10% Regular Early Bird after Sep 5
};

const getEarlyBirdPrice = (price) => {
  const discount = getEarlyBirdDiscount();
  return Math.round(price * (1 - discount));
};

const PricingSection = () => {
  const plans = [
    {
      id: "3K",
      name: "3K Fun Run",
      price: 799,
      description: "Perfect for beginners and families",
      icon: Zap,
      color: "#FFD700",
      features: [
        "Official Race BIB",
        "Event T-Shirt",
        "Finisher Medal",
        "Goodie Bag",
        "E-Certificate",
        "Refreshments"
      ]
    },
    {
      id: "5K",
      name: "5K Timed",
      price: 999,
      description: "Timed run with cash prizes for winners",
      icon: Award,
      color: "#FF3B30",
      popular: true,
      timed: true,
      features: [
        "Official Race BIB",
        "Event T-Shirt",
        "Finisher Medal",
        "Goodie Bag",
        "E-Certificate",
        "Refreshments",
        "Timed Run with Chip Timing",
        "Cash Prizes for Winners"
      ]
    },
    {
      id: "10K",
      name: "10K Timed",
      price: 1199,
      description: "Competitive timed run for serious runners",
      icon: Trophy,
      color: "#007AFF",
      timed: true,
      features: [
        "Official Race BIB",
        "Event T-Shirt",
        "Finisher Medal",
        "Goodie Bag",
        "E-Certificate",
        "Refreshments",
        "Timed Run with Chip Timing",
        "Cash Prizes for Winners"
      ]
    }
  ];

  return (
    <section id="pricing" className="section-spacing bg-[#FAFAFA]" data-testid="pricing-section">
      <div className="container-custom">
        {/* Super Early Bird Banner */}
        {isEarlyBirdActive() && (
          <div className="bg-gradient-to-r from-[#00C853] to-[#00E676] rounded-2xl p-6 mb-12 text-center text-white">
            <p className="text-2xl font-bold mb-2">🎉 SUPER EARLY BIRD OFFER — {Math.round(getEarlyBirdDiscount() * 100)}% OFF ALL TICKETS!</p>
            <p className="text-white/90">Offer ends September 5th at 11:59 PM. Don't miss out!</p>
          </div>
        )}

        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="text-[#007AFF] text-sm uppercase tracking-[0.2em] font-bold mb-4">
            Hyderabad 2027 - 3rd Edition
          </p>
          <h2 className="text-4xl sm:text-5xl font-bold text-[#0A0A0A] font-['Outfit'] mb-4">
            Choose Your Challenge
          </h2>
          <p className="text-[#4B5563] text-lg max-w-2xl mx-auto">
            Select the distance that matches your fitness level. Every runner receives premium perks.
          </p>
          <p className="text-[#FF3B30] font-semibold mt-4">
            February 7, 2027 • Gachibowli Stadium, Hyderabad
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {plans.map((plan) => {
            const Icon = plan.icon;
            
            return (
              <div
                key={plan.id}
                className={`relative bg-white rounded-3xl p-8 card-hover ${
                  plan.popular 
                    ? "ring-2 ring-[#FF3B30] shadow-2xl scale-105" 
                    : "shadow-lg"
                }`}
                data-testid={`pricing-${plan.id.toLowerCase()}-card`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#FF3B30] text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full">
                    Most Popular
                  </div>
                )}

                {/* Timed Badge */}
                {plan.timed && (
                  <div className="absolute top-4 right-4 bg-[#007AFF]/10 text-[#007AFF] text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1">
                    <Timer className="w-3 h-3" />
                    Timed
                  </div>
                )}

                {/* Icon */}
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                  style={{ backgroundColor: `${plan.color}20` }}
                >
                  <Icon className="w-8 h-8" style={{ color: plan.color }} />
                </div>

                {/* Plan Name */}
                <h3 className="text-2xl font-bold font-['Outfit'] text-[#0A0A0A] mb-2">
                  {plan.name}
                </h3>
                <p className="text-[#4B5563] text-sm mb-6">{plan.description}</p>

                {/* Price */}
                <div className="mb-6">
                  {isEarlyBirdActive() ? (
                    <>
                      <span className="text-4xl font-black font-['Outfit'] text-[#00C853]">
                        ₹{getEarlyBirdPrice(plan.price)}
                      </span>
                      <span className="text-lg text-gray-400 line-through ml-2">
                        ₹{plan.price}
                      </span>
                      <p className="text-[#00C853] text-xs font-semibold mt-1">
                        🎉 {getEarlyBirdDiscount() === 0.20 ? 'Super ' : ''}Early Bird - Save ₹{plan.price - getEarlyBirdPrice(plan.price)}!
                      </p>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-black font-['Outfit'] text-[#0A0A0A]">
                        ₹{plan.price}
                      </span>
                      <span className="text-[#9CA3AF] text-sm ml-2">per person</span>
                    </>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: `${plan.color}20` }}
                      >
                        <Check className="w-3 h-3" style={{ color: plan.color }} />
                      </div>
                      <span className="text-[#4B5563] text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Link to="/register/hyderabad-2027">
                  <Button
                    className={`w-full transition-all text-sm font-semibold gap-2 px-4 py-6 rounded-xl ${
                      plan.popular 
                        ? "btn-primary" 
                        : "bg-[#111827] hover:bg-[#1F2937] text-white"
                    }`}
                    data-testid={`register-${plan.id.toLowerCase()}-btn`}
                  >
                    Register Now
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            );
          })}
        </div>

        {/* Bottom Note */}
        <p className="text-center text-[#9CA3AF] text-sm mt-12">
          Group discounts available for corporate registrations. Contact us for more details.
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
